import { useEffect, useState } from 'react';
import { Spin, Empty, Tag } from 'antd';
import {
  UserOutlined, ShopOutlined, AppstoreOutlined, ShoppingCartOutlined,
  DollarOutlined, FileTextOutlined, HeartOutlined, RiseOutlined,
} from '@ant-design/icons';
import api from '../../api/axios';
import './admin.css';

const fmt = (v) => Number(v || 0).toLocaleString('uz-UZ');
const fmtShort = (v) => {
  const n = Number(v || 0);
  if (n >= 1e9) return (n / 1e9).toFixed(1) + ' mlrd';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + ' mln';
  if (n >= 1e3) return (n / 1e3).toFixed(0) + ' ming';
  return String(n);
};

const STATUS_LABEL = {
  pending: 'Kutilmoqda', confirmed: 'Tasdiqlangan', in_progress: 'Jarayonda',
  completed: 'Bajarilgan', cancelled: 'Bekor qilingan',
};
const STATUS_COLOR = {
  pending: '#fa8c16', confirmed: '#1890ff', in_progress: '#13c2c2',
  completed: '#52c41a', cancelled: '#f5222d',
};
const DIST_COLORS = ['#5F33E1', '#8B5CF6', '#EC4899', '#FB923C', '#10B981', '#06B6D4', '#F59E0B', '#6366F1'];

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admingov/stats/overview')
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  if (!data) return <Empty description="Ma'lumot yuklanmadi" style={{ padding: 80 }} />;

  const kpis = [
    { k: 'Foydalanuvchilar', v: fmt(data.totalUsers), icon: <UserOutlined />, c: '#5F33E1' },
    { k: 'Bizneslar', v: fmt(data.totalBusiness), icon: <ShopOutlined />, c: '#10B981' },
    { k: 'Xizmatlar', v: fmt(data.totalServices), icon: <AppstoreOutlined />, c: '#8B5CF6' },
    { k: 'Buyurtmalar', v: fmt(data.totalOrders), icon: <ShoppingCartOutlined />, c: '#FB923C' },
    { k: 'Umumiy daromad', v: fmtShort(data.totalRevenue) + " so'm", icon: <DollarOutlined />, c: '#06B6D4' },
    { k: 'Shikoyatlar', v: fmt(data.totalReports), icon: <FileTextOutlined />, c: '#F43F5E' },
    { k: "To'y rejalari", v: fmt(data.totalWeddings), icon: <HeartOutlined />, c: '#EC4899' },
  ];

  const monthly = data.monthlyOrders || [];
  const maxCount = Math.max(1, ...monthly.map(m => Number(m.count) || 0));

  const renderHBars = (rows, labelKey, valKey) => {
    const max = Math.max(1, ...rows.map(r => Number(r[valKey]) || 0));
    return (
      <div className="admin-hbars">
        {rows.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ma'lumot yo'q" />}
        {rows.map((r, i) => (
          <div className="admin-hbar-row" key={i}>
            <div className="admin-hbar-top">
              <span className="lbl">{r[labelKey] || '—'}</span>
              <span className="val">{fmt(r[valKey])}</span>
            </div>
            <div className="admin-hbar-track">
              <div className="admin-hbar-fill"
                style={{ width: `${(Number(r[valKey]) / max) * 100}%`, background: DIST_COLORS[i % DIST_COLORS.length] }} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      {/* KPI cards */}
      <div className="admin-kpi-grid">
        {kpis.map((c, i) => (
          <div className="admin-kpi" key={i}>
            <div className="admin-kpi-icon" style={{ background: c.c + '1f', color: c.c }}>{c.icon}</div>
            <div>
              <div className="admin-kpi-v">{c.v}</div>
              <div className="admin-kpi-k">{c.k}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Monthly trend + Order statuses */}
      <div className="admin-grid-2">
        <div className="admin-panel">
          <h3 className="admin-panel-title"><RiseOutlined /> Oylik buyurtmalar trendi (6 oy)</h3>
          {monthly.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ma'lumot yo'q" />
          ) : (
            <div className="admin-bars">
              {monthly.map((m, i) => (
                <div className="admin-bar-col" key={i}>
                  <div className="admin-bar-val">{m.count}</div>
                  <div className="admin-bar-track">
                    <div className="admin-bar" style={{ height: `${(Number(m.count) / maxCount) * 100}%` }}
                      title={`${fmt(m.revenue)} so'm`} />
                  </div>
                  <div className="admin-bar-lbl">{String(m.month).slice(2)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="admin-panel">
          <h3 className="admin-panel-title"><ShoppingCartOutlined /> Buyurtma statuslari</h3>
          <div className="admin-hbars">
            {(data.orderStatusStats || []).length === 0 && (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ma'lumot yo'q" />
            )}
            {(data.orderStatusStats || []).map((s, i) => {
              const max = Math.max(1, ...data.orderStatusStats.map(x => Number(x.count) || 0));
              return (
                <div className="admin-hbar-row" key={i}>
                  <div className="admin-hbar-top">
                    <span className="lbl">
                      <Tag color={STATUS_COLOR[s.status]} style={{ marginInlineEnd: 0 }}>
                        {STATUS_LABEL[s.status] || s.status}
                      </Tag>
                    </span>
                    <span className="val">{fmt(s.count)}</span>
                  </div>
                  <div className="admin-hbar-track">
                    <div className="admin-hbar-fill"
                      style={{ width: `${(Number(s.count) / max) * 100}%`, background: STATUS_COLOR[s.status] || '#888' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Regions + Service types */}
      <div className="admin-grid-2 even">
        <div className="admin-panel">
          <h3 className="admin-panel-title"><ShopOutlined /> Viloyat bo'yicha xizmatlar</h3>
          {renderHBars((data.regionStats || []).slice(0, 8), 'region', 'services_count')}
        </div>
        <div className="admin-panel">
          <h3 className="admin-panel-title"><AppstoreOutlined /> Xizmat turlari</h3>
          {renderHBars((data.serviceTypeStats || []).slice(0, 8), 'type_name', 'count')}
        </div>
      </div>
    </div>
  );
}
